/**
 * Player - Refactored with State Machine Pattern
 * The big blue ball controlled by the player
 */

import Phaser from 'phaser';
import StateMachine from '../core/StateMachine';
import {
    IdleState,
    RollingState,
    JumpingState,
    FallingState,
    DeadState,
    PlayerStates
} from './states';
import eventBus, { GameEvents } from '../core/EventBus';
import serviceLocator, { Services } from '../core/ServiceLocator';
import { PHYSICS, DASH } from '../configs/GameConfig';

export default class Player {
    constructor(scene, x, y) {
        this.scene = scene;
        this.isDead = false;

        // Get audio manager from ServiceLocator (reduces coupling)
        this.audioManager = serviceLocator.get(Services.AUDIO_MANAGER);

        // Create visuals
        this.createVisuals(x, y);

        // Create physics body
        this.createPhysicsBody(x, y);

        // Setup input
        this.setupInput();

        // Jump tracking
        this.jumpCount = 0;
        this.isGrounded = false;
        this.lastGroundedTime = 0;
        this.coyoteTime = 100;

        // Visual scale
        this.baseScaleX = 1;
        this.baseScaleY = 1;

        // Magnetic pull state tracking
        this.isPulling = false;
        this.pullTarget = null;
        this.wasPulling = false;

        // Dash state tracking
        this.isDashing = false;
        this.dashEndTime = 0;
        this.lastDashTime = -Infinity;
        this.lastMoveDir = 1;

        // Setup collision detection
        this.setupCollisionDetection();

        // Initialize State Machine
        this.initStateMachine();

        // Emit player state change for UI
        eventBus.emit(GameEvents.PLAYER_STATE_CHANGE, { state: PlayerStates.IDLE });
    }

    createVisuals(x, y) {
        // Main sprite - the blue ball
        this.sprite = this.scene.add.circle(x, y, 22, 0x0066ff);

        // Highlight for 3D effect
        this.highlight = this.scene.add.circle(x - 5, y - 5, 8, 0x66b3ff, 0.7);

        // Shadow beneath player
        this.shadow = this.scene.add.ellipse(x, y + 25, 30, 10, 0x000000, 0.3);

        // Magnetic pull beam graphic
        this.pullBeam = this.scene.add.graphics();
        this.pullBeam.setVisible(false);
    }

    createPhysicsBody(x, y) {
        const { Bodies } = Phaser.Physics.Matter.Matter;

        // Optimized physics body for smooth rolling without jittering
        const playerBody = Bodies.circle(x, y, PHYSICS.PLAYER.RADIUS, {
            restitution: PHYSICS.PLAYER.RESTITUTION,
            friction: PHYSICS.PLAYER.FRICTION,
            frictionStatic: PHYSICS.PLAYER.FRICTION_STATIC,
            frictionAir: PHYSICS.PLAYER.FRICTION_AIR,
            density: PHYSICS.PLAYER.DENSITY,
            label: 'player',
            slop: PHYSICS.PLAYER.SLOP,
            // Continuous collision detection for high-speed movement
            sleepThreshold: 60,       // How easily the body goes to sleep
            timeScale: 1
        });

        this.scene.matter.add.gameObject(this.sprite, playerBody);

        // Enable continuous collision detection (CCD) equivalent
        // This prevents tunneling at high speeds
        this.sprite.body.collisionFilter = {
            category: 0x0001,
            mask: 0xFFFF,
            group: 0
        };
    }

    setupInput() {
        // Arrow keys
        this.cursors = this.scene.input.keyboard.createCursorKeys();

        // WASD
        this.wasd = {
            up: this.scene.input.keyboard.addKey('W'),
            down: this.scene.input.keyboard.addKey('S'),
            left: this.scene.input.keyboard.addKey('A'),
            right: this.scene.input.keyboard.addKey('D')
        };

        this.spaceKey = this.scene.input.keyboard.addKey('SPACE');
        this.dashKey = this.scene.input.keyboard.addKey('SHIFT');
    }

    /**
     * Update dash state and apply dash velocity
     * @param {boolean} moveLeft
     * @param {boolean} moveRight
     * @returns {boolean} true if currently dashing
     */
    handleDash(moveLeft, moveRight) {
        if (moveLeft) this.lastMoveDir = -1;
        if (moveRight) this.lastMoveDir = 1;

        const now = this.scene.time.now;
        const dashPressed = Phaser.Input.Keyboard.JustDown(this.dashKey);
        const canDash = now - this.lastDashTime >= DASH.COOLDOWN;

        if (dashPressed && canDash) {
            this.isDashing = true;
            this.dashEndTime = now + DASH.DURATION;
            this.lastDashTime = now;
        }

        if (this.isDashing) {
            if (now >= this.dashEndTime) {
                this.isDashing = false;
                return false;
            }

            this.sprite.setVelocityX(this.lastMoveDir * DASH.SPEED);
            return true;
        }

        return false;
    }

    setupCollisionDetection() {
        this.scene.matter.world.on('collisionstart', (event) => {
            event.pairs.forEach((pair) => {
                const { bodyA, bodyB } = pair;
                if (bodyA === this.sprite.body || bodyB === this.sprite.body) {
                    const other = bodyA === this.sprite.body ? bodyB : bodyA;

                    // Ground detection - player is above the other object
                    if (this.sprite.body.position.y < other.position.y) {
                        this.isGrounded = true;
                        this.jumpCount = 0;
                        this.lastGroundedTime = this.scene.time.now;
                    }
                }
            });
        });

        this.scene.matter.world.on('collisionend', (event) => {
            event.pairs.forEach((pair) => {
                const { bodyA, bodyB } = pair;
                if (bodyA === this.sprite.body || bodyB === this.sprite.body) {
                    this.isGrounded = false;
                }
            });
        });
    }

    initStateMachine() {
        this.stateMachine = new StateMachine(this);

        // Add all states
        this.stateMachine.addState(PlayerStates.IDLE, new IdleState());
        this.stateMachine.addState(PlayerStates.ROLLING, new RollingState());
        this.stateMachine.addState(PlayerStates.JUMPING, new JumpingState());
        this.stateMachine.addState(PlayerStates.FALLING, new FallingState());
        this.stateMachine.addState(PlayerStates.DEAD, new DeadState());

        // Start in idle state
        this.stateMachine.start(PlayerStates.IDLE);
    }

    /**
     * Check if jump input was just pressed
     */
    checkJumpInputJustPressed() {
        return Phaser.Input.Keyboard.JustDown(this.cursors.up) ||
            Phaser.Input.Keyboard.JustDown(this.wasd.up) ||
            Phaser.Input.Keyboard.JustDown(this.spaceKey);
    }

    /**
     * Check if jump conditions are met (used by states)
     */
    checkJumpInput() {
        if (!this.checkJumpInputJustPressed()) return false;

        const canCoyoteJump = (this.scene.time.now - this.lastGroundedTime) < this.coyoteTime;
        const canJump = (this.isGrounded || canCoyoteJump) && this.jumpCount === 0;

        return canJump;
    }

    /**
     * Handle magnetic pull mechanic
     */
    handleMagneticPull() {
        const pullKeyHeld = this.cursors.down.isDown || this.wasd.down.isDown;
        this.updateMagneticPull(pullKeyHeld);
    }

    updateMagneticPull(isActive) {
        const pullRange = PHYSICS.MAGNETIC.PULL_RANGE;
        const pullForce = PHYSICS.MAGNETIC.PULL_FORCE;
        const maxVelocity = PHYSICS.MAGNETIC.MAX_VELOCITY;
        const currentTime = this.scene.time.now;

        this.pullBeam.clear();

        // Find nearby box (regular or magnetic)
        const bodies = this.scene.matter.world.getAllBodies();
        let closestBox = null;
        let closestDist = pullRange;
        let isMagnetic = false;

        for (const body of bodies) {
            if (body.label === 'box' || body.label === 'magneticBox') {
                const dx = body.position.x - this.sprite.x;
                const dy = body.position.y - this.sprite.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < closestDist) {
                    closestDist = dist;
                    closestBox = body;
                    isMagnetic = body.label === 'magneticBox';
                }
            }
        }

        // If not active or no target, stabilize magnetic box and hide beam
        if (!isActive || !closestBox) {
            if (this.wasPulling && this.pullTarget && this.pullTarget.label === 'magneticBox') {
                this.stabilizeMagneticBox(this.pullTarget);
            }

            this.pullBeam.setVisible(false);
            this.isPulling = false;
            this.pullTarget = null;
            this.wasPulling = false;
            return;
        }

        this.isPulling = true;
        this.pullTarget = closestBox;
        this.wasPulling = true;

        // Enable gravity on magnetic box when player grabs it (only once)
        if (isMagnetic && closestBox.ignoreGravity) {
            closestBox.ignoreGravity = false;
        }

        // Draw beam effect (enhanced for magnetic boxes)
        this.drawPullBeam(closestBox, isMagnetic, currentTime);

        // Emit box pulled event (throttled to prevent spam)
        if (!this._lastPullEventTime || currentTime - this._lastPullEventTime > 250) {
            eventBus.emit(GameEvents.BOX_PULLED, {
                boxPosition: closestBox.position,
                playerPosition: { x: this.sprite.x, y: this.sprite.y },
                isMagnetic: isMagnetic
            });
            this._lastPullEventTime = currentTime;
        }

        // Apply pull force toward player with velocity clamp
        const dx = this.sprite.x - closestBox.position.x;
        const dy = this.sprite.y - closestBox.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 30) {
            // Distance-based force: stronger when far, weaker when close
            const distanceFactor = Math.min(1, (dist - 30) / 200);
            const adjustedForce = pullForce * distanceFactor;
            
            const fx = (dx / dist) * adjustedForce;
            const fy = (dy / dist) * adjustedForce;
            this.scene.matter.body.applyForce(closestBox, closestBox.position, { x: fx, y: fy });
        }

        // Clamp velocity for all boxes (both regular and magnetic)
        const vx = Phaser.Math.Clamp(closestBox.velocity.x, -maxVelocity, maxVelocity);
        const vy = Phaser.Math.Clamp(closestBox.velocity.y, -maxVelocity, maxVelocity);
        this.scene.matter.body.setVelocity(closestBox, { x: vx, y: vy });

        // Stabilize angular velocity for magnetic boxes
        if (isMagnetic) {
            this.scene.matter.body.setAngularVelocity(closestBox, 0);
        }
    }

    /**
     * Stabilize magnetic box when player releases pull
     */
    stabilizeMagneticBox(box) {
        if (!box || box.label !== 'magneticBox') return;

        // Don't fully stop - let it drift slightly for natural feel
        const vx = box.velocity.x * 0.6;
        const vy = box.velocity.y * 0.6;
        this.scene.matter.body.setVelocity(box, { x: vx, y: vy });
        this.scene.matter.body.setAngularVelocity(box, 0);
    }

    drawPullBeam(closestBox, isMagnetic = false, currentTime = 0) {
        this.pullBeam.setVisible(true);
        this.pullBeam.clear();

        const bx = closestBox.position.x;
        const by = closestBox.position.y;
        const px = this.sprite.x;
        const py = this.sprite.y;

        const time = (currentTime || this.scene.time.now) * 0.01;
        const segments = 8;

        // Draw beam segments
        for (let i = 0; i < segments; i++) {
            const t1 = i / segments;
            const t2 = (i + 1) / segments;
            const x1 = px + (bx - px) * t1;
            const y1 = py + (by - py) * t1;
            const x2 = px + (bx - px) * t2;
            const y2 = py + (by - py) * t2;

            const wave = Math.sin(time + i * 0.5) * 3;
            const alpha = 0.8 - (i * 0.08);
            const beamColor = isMagnetic ? 0x00aaff : 0x00ffff;

            this.pullBeam.lineStyle(4 - i * 0.3, beamColor, alpha);
            this.pullBeam.beginPath();
            this.pullBeam.moveTo(x1, y1 + wave);
            this.pullBeam.lineTo(x2, y2 + wave);
            this.pullBeam.strokePath();
        }

        // Sparkles removed to prevent stray particle artifacts
    }

    /**
     * Apply squash/stretch effect
     */
    applySquash(x, y) {
        this.baseScaleX = x;
        this.baseScaleY = y;

        this.scene.tweens.add({
            targets: this,
            baseScaleX: 1,
            baseScaleY: 1,
            duration: 200,
            ease: 'Elastic.Out'
        });
    }

    /**
     * Create jump particles
     */
    createJumpParticles() {
        for (let i = 0; i < 5; i++) {
            const particle = this.scene.add.circle(
                this.sprite.x + Phaser.Math.Between(-15, 15),
                this.sprite.y + 20,
                Phaser.Math.Between(3, 6),
                0xffffff,
                0.8
            );

            this.scene.tweens.add({
                targets: particle,
                y: particle.y + 30,
                alpha: 0,
                scale: 0.5,
                duration: 300,
                onComplete: () => particle.destroy()
            });
        }
    }

    /**
     * Create double jump particles (ring effect)
     */
    createDoubleJumpParticles() {
        for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * Math.PI * 2;
            const particle = this.scene.add.circle(
                this.sprite.x,
                this.sprite.y,
                4,
                0x00ffff,
                0.9
            );

            this.scene.tweens.add({
                targets: particle,
                x: this.sprite.x + Math.cos(angle) * 40,
                y: this.sprite.y + Math.sin(angle) * 40,
                alpha: 0,
                scale: 0.3,
                duration: 400,
                onComplete: () => particle.destroy()
            });
        }

        // Center flash
        const flash = this.scene.add.circle(this.sprite.x, this.sprite.y, 15, 0x00ffff, 0.8);
        this.scene.tweens.add({
            targets: flash,
            scale: 3,
            alpha: 0,
            duration: 300,
            onComplete: () => flash.destroy()
        });
    }

    /**
     * Create landing dust particles - subtle puff effect when landing from height
     * @param {number} impactVelocity - How fast the player was falling
     */
    createLandingDust(impactVelocity = 5) {
        // Scale particle count based on impact velocity
        const particleCount = Math.min(Math.floor(Math.abs(impactVelocity) * 0.8), 12);

        if (particleCount < 2) return; // Don't show for small impacts

        const groundY = this.sprite.y + 20;

        // Create dust cloud
        for (let i = 0; i < particleCount; i++) {
            const side = i < particleCount / 2 ? -1 : 1;
            const spreadX = Phaser.Math.Between(5, 25) * side;

            // Dust particle - subtle earth/sand color
            const dust = this.scene.add.ellipse(
                this.sprite.x + Phaser.Math.Between(-8, 8),
                groundY,
                Phaser.Math.Between(4, 8),
                Phaser.Math.Between(3, 5),
                0x8B7355,  // Dusty brown
                Phaser.Math.FloatBetween(0.3, 0.6)
            );

            // Animate dust spreading outward and upward
            this.scene.tweens.add({
                targets: dust,
                x: dust.x + spreadX,
                y: groundY - Phaser.Math.Between(10, 25),
                alpha: 0,
                scaleX: Phaser.Math.FloatBetween(1.5, 2.5),
                scaleY: Phaser.Math.FloatBetween(0.5, 1),
                duration: Phaser.Math.Between(300, 500),
                ease: 'Power2.easeOut',
                onComplete: () => dust.destroy()
            });
        }

        // Optional: Add a quick ground "poof" circle
        if (Math.abs(impactVelocity) > 8) {
            const poof = this.scene.add.circle(this.sprite.x, groundY, 5, 0xAA9977, 0.4);
            this.scene.tweens.add({
                targets: poof,
                scaleX: 4,
                scaleY: 1.5,
                alpha: 0,
                duration: 250,
                ease: 'Power2.easeOut',
                onComplete: () => poof.destroy()
            });
        }
    }

    /**
     * Kill the player
     */
    die(cause = 'unknown') {
        if (this.isDead) return;
        this.stateMachine.setState(PlayerStates.DEAD, { cause });
    }

    /**
     * Get current state name
     */
    getCurrentState() {
        return this.stateMachine.getCurrentStateName();
    }

    /**
     * Main update loop
     */
    update() {
        if (this.isDead) return;

        // Update state machine
        this.stateMachine.update(this.scene.game.loop.delta);

        // Update visual positions
        this.updateVisuals();

        // Update rolling sound based on ground contact and velocity
        if (this.audioManager && this.isGrounded && this.sprite.body) {
            this.audioManager.updateRollingSound(this.sprite.body.velocity.x);
        } else if (this.audioManager) {
            this.audioManager.stopRollingSound();
        }
    }

    updateVisuals() {
        // Sync highlight with sprite
        this.highlight.x = this.sprite.x - 5;
        this.highlight.y = this.sprite.y - 5;

        // Sync shadow
        this.shadow.x = this.sprite.x;
        this.shadow.y = this.sprite.y + 25;

        // Scale shadow based on height
        const velocity = this.sprite.body.velocity;
        const shadowScale = Math.max(0.3, 1 - (velocity.y * 0.02));
        this.shadow.setScale(shadowScale, shadowScale * 0.4);
    }

    /**
     * Cleanup
     */
    destroy() {
        if (this.stateMachine) {
            this.stateMachine.destroy();
        }
        if (this.sprite) {
            this.sprite.destroy();
        }
        if (this.highlight) {
            this.highlight.destroy();
        }
        if (this.shadow) {
            this.shadow.destroy();
        }
        if (this.pullBeam) {
            this.pullBeam.destroy();
        }
    }
}
